/**
 * StatGrid — responsive container voor KPI-kaarten (R11 design system).
 * Implementeert de KPI-grid-regel uit het contract: 1 → 2 → 4 koloms.
 *
 * Props:
 *   children   — StatCard-elementen
 *   cols       — max kolommen op lg: 2 | 3 | 4 (default 4)
 *   className  — extra classes (optioneel)
 */
export function StatGrid({ children, cols = 4, className = '' }) {
  const lgCols = {
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
  }[cols] ?? 'lg:grid-cols-4'

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${lgCols} gap-4 ${className}`}>
      {children}
    </div>
  )
}

/**
 * StatCard — enkele KPI-kaart.
 *
 * Props:
 *   label      — uppercase label (verplicht)
 *   value      — hoofdwaarde, in font-mono (verplicht)
 *   icon       — Lucide-component, decoratief (optioneel)
 *   subtitle   — subtekst onderaan (optioneel)
 *   tone       — 'default' | 'warning' | 'success'; kleurt ALLEEN de subtitel
 *   progress   — 0-100; dunne accent-balk onder de waarde (optioneel)
 *   onClick    — maakt de kaart klikbaar (optioneel, a11y-correct)
 *   className  — extra classes (optioneel)
 *
 * B0: .r11-kpi heeft border-l-4 ZONDER kleur -> we voegen border-l-accent toe
 * (één-accent-regel). Dark-mode komt via .r11-kpi -> .r11-card (geen dubbele
 * dark:-classes nodig).
 */
export function StatCard({
  label,
  value,
  icon: Icon,
  subtitle,
  tone = 'default',
  progress,
  onClick,
  className = '',
}) {
  const toneClass = {
    default: '',
    warning: 'text-amber-600',
    success: 'text-green-600',
  }[tone] ?? ''

  const clickable = typeof onClick === 'function'
  const interactiveProps = clickable
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick(e)
          }
        },
      }
    : {}

  return (
    <div
      {...interactiveProps}
      className={`r11-kpi border-l-accent
        ${clickable ? 'cursor-pointer hover:border-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40' : ''}
        ${className}`}
    >
      <div className="flex items-center justify-between mb-3.5">
        <span className="r11-kpi__eyebrow">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-300" aria-hidden="true" />}
      </div>
      <div className="r11-kpi__value font-mono">{value}</div>
      {progress !== undefined && (
        <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-accent transition-all"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      )}
      {subtitle && <p className={`r11-kpi__sub ${toneClass}`}>{subtitle}</p>}
    </div>
  )
}
